// CAPTCHA support for the hosted auth-ui. Mirrors the admin-portal's
// implementation (script loader + invisible-execute hook + provider type
// shape) but trimmed to the end-user surface this app actually exposes.
// Types deliberately copied rather than imported from `@pryma/auth-shared`
// so this app stays free of a workspace dep on the shared types package —
// the surface is small and stable enough to duplicate here.

import { useCallback, useEffect, useRef, useState } from 'react';

import { getEndUserCaptchaConfig } from './api';

export type CaptchaProvider = 'turnstile' | 'recaptcha_v3' | 'hcaptcha';

// Endpoints the auth-ui can challenge on. Mirrors the end-user subset of
// CaptchaConfiguration::ENDPOINTS in the Rails API.
export type CaptchaEndpoint =
  | 'end_user_login'
  | 'end_user_register'
  | 'end_user_magic_link'
  | 'end_user_otp';

// Public-facing config returned by /api/auth/projects/:id/captcha-config.
export interface CaptchaConfigPublic {
  provider: CaptchaProvider | null;
  site_key: string | null;
  enabled: boolean;
  enabled_endpoints: CaptchaEndpoint[];
  score_threshold: number | null;
}

// Whether the project has CAPTCHA enabled for a given end-user endpoint.
// Centralized so callers don't have to repeat the null/enabled checks.
export function isCaptchaRequired(
  config: CaptchaConfigPublic | null,
  endpoint: CaptchaEndpoint
): boolean {
  if (!config || !config.enabled || !config.provider || !config.site_key) return false;
  return config.enabled_endpoints.includes(endpoint);
}

// Minimal subsets of each provider's window-scoped global. We hand-roll
// these rather than pulling in three sets of provider type packages.
interface TurnstileGlobal {
  render: (
    container: HTMLElement | string,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'compact' | 'invisible';
      action?: string;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  execute: (widgetId: string) => void;
}

interface RecaptchaV3Global {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

interface HcaptchaGlobal {
  render: (
    container: HTMLElement,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark';
      size?: 'normal' | 'compact' | 'invisible';
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  execute: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
    grecaptcha?: RecaptchaV3Global;
    hcaptcha?: HcaptchaGlobal;
  }
}

const SCRIPT_URLS: Record<CaptchaProvider, (siteKey: string) => string> = {
  turnstile: () => 'https://challenges.cloudflare.com/turnstile/v0/api.js',
  // reCAPTCHA v3 needs the site key in the script URL — that's how it
  // installs the always-on observer.
  recaptcha_v3: siteKey =>
    `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`,
  hcaptcha: () => 'https://js.hcaptcha.com/1/api.js',
};

const SCRIPT_ID: Record<CaptchaProvider, string> = {
  turnstile: 'pryma-turnstile-script',
  recaptcha_v3: 'pryma-recaptcha-v3-script',
  hcaptcha: 'pryma-hcaptcha-script',
};

// Cache of in-flight script loads so we don't insert the same <script> twice
// when multiple widgets/hooks mount in the same render.
const loadPromises = new Map<string, Promise<void>>();

export function loadProviderScript(provider: CaptchaProvider, siteKey: string): Promise<void> {
  const id = SCRIPT_ID[provider];
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('document is not defined'));
  }

  const cached = loadPromises.get(id);
  if (cached) return cached;

  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing) {
    const ready = Promise.resolve();
    loadPromises.set(id, ready);
    return ready;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = SCRIPT_URLS[provider](siteKey);
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromises.delete(id);
      reject(new Error(`Failed to load ${provider} script`));
    };
    document.head.appendChild(script);
  });

  loadPromises.set(id, promise);
  return promise;
}

// Test-only: clear the load-promise cache so successive tests start fresh.
export function _resetCaptchaScriptCache(): void {
  loadPromises.clear();
}

interface UseCaptchaOptions {
  provider: CaptchaProvider;
  siteKey: string;
  // Required for reCAPTCHA v3, optional for others.
  action?: string;
  onError?: (err: Error) => void;
}

interface UseCaptchaResult {
  isReady: boolean;
  // For invisible execution (reCAPTCHA v3). Returns null when the provider
  // is interactive and produces tokens via its own widget callback.
  execute: () => Promise<string | null>;
}

// Hook for invisible/imperative CAPTCHA execution. For interactive widget
// rendering, use <CaptchaWidget />.
export function useCaptcha({
  provider,
  siteKey,
  action = 'submit',
  onError,
}: UseCaptchaOptions): UseCaptchaResult {
  const [isReady, setIsReady] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) {
      setIsReady(false);
      return;
    }
    loadProviderScript(provider, siteKey)
      .then(() => {
        if (cancelled) return;
        if (provider === 'recaptcha_v3' && window.grecaptcha) {
          window.grecaptcha.ready(() => {
            if (!cancelled) setIsReady(true);
          });
        } else {
          setIsReady(true);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) onErrorRef.current?.(err);
      });
    return () => {
      cancelled = true;
    };
  }, [provider, siteKey]);

  const execute = useCallback(async (): Promise<string | null> => {
    if (!isReady) return null;
    try {
      if (provider === 'recaptcha_v3' && window.grecaptcha) {
        return await window.grecaptcha.execute(siteKey, { action });
      }
      return null;
    } catch (err) {
      onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }, [provider, siteKey, action, isReady]);

  return { isReady, execute };
}

// Fetch the project's CAPTCHA config once at mount. Returns 'pending' while
// the network is in flight and 'ready' afterwards (with config=null on
// error or when no project_id is supplied — the auth-ui treats both as
// "no challenge" and lets the API enforce on the server side).
export type CaptchaConfigStatus = 'pending' | 'ready';

export function useCaptchaConfig(projectId: string | null): {
  status: CaptchaConfigStatus;
  config: CaptchaConfigPublic | null;
} {
  const [status, setStatus] = useState<CaptchaConfigStatus>('pending');
  const [config, setConfig] = useState<CaptchaConfigPublic | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setStatus('ready');
      setConfig(null);
      return;
    }

    getEndUserCaptchaConfig(projectId)
      .then(result => {
        if (cancelled) return;
        if (result.success && result.data) {
          setConfig(result.data);
        }
      })
      .catch(() => {
        // Silent fallback — auth must still load on a captcha-config outage.
      })
      .finally(() => {
        if (!cancelled) setStatus('ready');
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return { status, config };
}
